import { gql } from '@apollo/client';
import { TRANSLATED_STRING_FIELDS } from '@assets/i18n/config';

export type FetchUserPermissions = {
  canReadClientLowRiskPii: boolean;
  canReadClientHighRiskPii: boolean;
  canReadClientBilling: boolean;
  canReadClientSuitability: boolean;
  canReadIdVerification: boolean;
  canReadFraudRiskBasic: boolean;
  feeDataMigration?: boolean;
};

export const FETCH_USER = ({
  canReadClientLowRiskPii,
  canReadClientHighRiskPii,
  canReadClientBilling,
  canReadClientSuitability,
  canReadIdVerification,
  canReadFraudRiskBasic,
  feeDataMigration = false,
}: FetchUserPermissions) => gql`
  query fetchUser($userId: SafeString!) {
    fetchUser(input: { userId: $userId }) {
      user {
        id: stringId
        type
        typeOfTrust
        ${canReadClientLowRiskPii ? 'firstName' : ''}
        ${canReadClientLowRiskPii ? 'middleName' : ''}
        ${canReadClientLowRiskPii ? 'lastName' : ''}
        ${canReadClientLowRiskPii ? 'primaryEmail' : ''}
        ${canReadClientLowRiskPii ? 'phone' : ''}
        ${canReadClientHighRiskPii ? 'dateOfBirth' : ''}
        ${canReadClientHighRiskPii ? 'inProvinceSince' : ''}
        ${canReadClientHighRiskPii ? 'physicalAddress { city country houseNumber jurisdiction neighborhood postal streetName unitNumber }' : ''}
        ${canReadClientHighRiskPii ? 'gender' : ''}
        language
        state
        fatcaStatus
        fatcaStatusOtherDescription
        affiliateOnly
        organization {
          id
          translatedName { ${TRANSLATED_STRING_FIELDS} }
          isReferral
          allowPortfolioPerGoal
          blockSuspiciousTransactions
          reviewTransactions
          availableFeatureFlags
          applicableLocalization { countries }
          jurisdictions { all only }
        }
        statistics { marketValueCents }
        allIncompleteFormAgreements { id }
        ${
          canReadClientBilling
            ? feeDataMigration
              ? `feeSetting {
          feeTier { id name}
          applicableFeeTier { id name}
          billingSchedule { id frequency}
          applicableBillingSchedule { id frequency}
        }`
              : `feeTier { id name}
        applicableFeeTier { id name}
        billingSchedule { id frequency}
        applicableBillingSchedule { id frequency}`
            : ''
        }
        incompleteFields
        preferredMethodOfCommunication
        households {
          id: stringId
          name
          relationships {
            accessType
            user { id: stringId }
          }
        }

        # following attributes are used by <ClientDetails>
        ${canReadClientLowRiskPii ? 'spouseFirstName' : ''}
        ${canReadClientLowRiskPii ? 'spouseMiddleName' : ''}
        ${canReadClientLowRiskPii ? 'spouseLastName' : ''}
        ${canReadClientLowRiskPii ? 'spouseJobTitle' : ''}
        ${canReadClientLowRiskPii ? 'spouseCompanyType' : ''}
        ${canReadClientLowRiskPii ? 'spouseEmail' : ''}
        ${canReadClientLowRiskPii ? 'spouseEmploymentType' : ''}
        ${canReadClientLowRiskPii ? 'spouseEmployerName' : ''}
        ${canReadClientLowRiskPii ? 'numberOfDependents' : ''}
        ${canReadClientLowRiskPii ? 'spouseAddress { city country houseNumber jurisdiction neighborhood postal streetName unitNumber }' : ''}
        iDVerified
        iDCheckCompleted
        iDCheckRetrigger
        ${canReadClientLowRiskPii ? 'complianceState' : ''}
        ${canReadClientLowRiskPii ? 'employmentStatus' : ''}
        ${canReadClientLowRiskPii ? 'companyType' : ''}
        ${canReadClientLowRiskPii ? 'jobTitle' : ''}
        ${canReadClientLowRiskPii ? 'studentAreaOfStudy' : ''}
        ${canReadClientSuitability ? 'lastSuitabilityReviewAt' : ''}
        ${canReadClientSuitability ? 'employerName' : ''}
        ${canReadClientSuitability ? 'employmentSituation' : ''}
        ${canReadClientSuitability ? 'employmentSituationOtherDescription' : ''}
        ${canReadClientSuitability ? 'employerCompanyAddress { city country houseNumber jurisdiction neighborhood postal streetName unitNumber }' : ''}
        ${canReadIdVerification ? 'latestIdVerification { documentType documentIssuingJurisdication }' : ''}
        ${canReadClientSuitability ? 'annualIncomeCents' : ''}
        ${canReadClientSuitability ? 'annualIncomeStr' : ''}
        ${canReadClientSuitability ? 'financialLiquidAssetsCents' : ''}
        ${canReadClientSuitability ? 'financialFixedAssetsCents' : ''}
        ${canReadClientSuitability ? 'totalDebtCents' : ''}
        ${canReadClientSuitability ? 'annualDebtPaymentsCents' : ''}
        ${canReadClientSuitability ? 'isAccreditedInvestor' : ''}
        ${canReadClientSuitability ? 'investmentKnowledge' : ''}
        ${canReadClientSuitability ? 'riskQuestion1' : ''}
        ${canReadClientSuitability ? 'riskQuestion2' : ''}
        ${canReadClientSuitability ? 'suitabilityScore' : ''}
        ${canReadClientSuitability ? 'sourceOfWealth sourceOfFunds sourceOfWealthOtherDescription sourceOfFundsOtherDescription uniqueCircumstances' : ''}
        ${canReadClientLowRiskPii ? 'maritalStatus' : ''}
        ${canReadClientLowRiskPii ? 'citizenships' : ''}
        ${canReadClientHighRiskPii ? 'foreignTaxInformation { foreignTaxCountry foreignTaxNumber }' : ''}
        ${canReadFraudRiskBasic ? `fraudRisk { id lastUpdatedBy notes score riskReasons { translatedDescription { ${TRANSLATED_STRING_FIELDS} } } }` : ''}
        countryOfTaxResidence
        ${canReadClientLowRiskPii ? 'headOfInternationalOrganization' : ''}
        ${canReadClientLowRiskPii ? 'complianceIssueSource' : ''}
        ${canReadClientLowRiskPii ? `complianceIssueSourceTranslated { ${TRANSLATED_STRING_FIELDS} }` : ''}
        ${canReadClientLowRiskPii ? 'isOfficerOfPublicCompany' : ''}
        ${canReadClientLowRiskPii ? 'isOwnerOfPublicCompany' : ''}
        ${canReadClientLowRiskPii ? 'ssSymbols' : ''}
        ${canReadClientLowRiskPii ? 'isMemberOfIiroc' : ''}
        ${canReadClientLowRiskPii ? 'insiderSymbols' : ''}
        ${canReadClientLowRiskPii ? 'politicallyExposedDomesticPerson' : ''}
        ${canReadClientLowRiskPii ? 'politicallyExposedForeignPerson' : ''}
        ${canReadClientLowRiskPii ? 'closeAssociateOfPEP' : ''}
        ${canReadClientLowRiskPii ? 'powerOfAttorneyGranted' : ''}
        ${
          canReadClientLowRiskPii
            ? `trustedContactPerson {
        name email relation phone
        physicalAddress { city country houseNumber jurisdiction neighborhood postal streetName unitNumber }
      }`
            : ''
        }
        readyToSignAgreement
        isVulnerablePerson
        isOwnerOfEntity
        sinExists
        taxIdType
        taxIdExists

        # related to Non-individual entities
        entityName
        relatedEntities { relation }
        establishedDate
        countryOfRegistration
        registrationNumber
        taxNumber
        yearEnd
        isNonProfit
        receivesDonationsFromPublic
        charityRegistrationNumber
        isRegisteredWithCRA
        isEntityRegulated regulatorName firmNumber
        isReportingIssuer issuerName issuerTicker issuerExchange
        isAFinancialEntity isAffiliateOfFinancialEntity instructionsFromFinancialEntity
        isForThirdParty
        isPublicBody
        isLargeCorporation
      }
    }
  }
`;
